import moment from 'moment';
import { BaseEntity, MemoizeGetters, toDate, Transform } from './base';
import { EventType } from './integration';

export type DeliveryStatus = 'pending' | 'processing' | 'succeeded' | 'failed' | 'dead';

/** A single webhook delivery attempt. Detail responses add payload/headers/body. */
@MemoizeGetters
export class WebhookDelivery extends BaseEntity {
  eventId: string;
  eventType: EventType;
  status: DeliveryStatus;
  responseStatus?: number | null;
  lastError?: string | null;

  @Transform(toDate)
  deliveredAt?: Date | null;

  @Transform(toDate)
  createdAt: Date;

  @Transform(toDate)
  updatedAt: Date;

  // Detail-only fields
  requestPayload?: any;
  responseHeaders?: Record<string, string> | any;
  responseBody?: string;

  get createdAtFormatted(): string {
    return this.createdAt ? moment(this.createdAt).format('lll') : '';
  }

  get deliveredAtFormatted(): string {
    return this.deliveredAt ? moment(this.deliveredAt).format('lll') : '—';
  }

  get isResendable(): boolean {
    return this.status === 'failed' || this.status === 'dead';
  }

  /** Badge theme matching ysh-badge ('mint' | 'coral' | 'canary' | 'smoke'). */
  get statusBadgeTheme(): 'mint' | 'coral' | 'canary' | 'smoke' {
    switch (this.status) {
      case 'succeeded':
        return 'mint';
      case 'pending':
      case 'processing':
        return 'canary';
      case 'failed':
      case 'dead':
        return 'coral';
      default:
        return 'smoke';
    }
  }

  get requestPayloadPretty(): string {
    return this.prettyJson(this.requestPayload);
  }

  get responseHeadersPretty(): string {
    return this.prettyJson(this.responseHeaders);
  }

  private prettyJson(value: any): string {
    if (value == null) {
      return '';
    }
    if (typeof value === 'string') {
      try {
        return JSON.stringify(JSON.parse(value), null, 2);
      } catch {
        return value;
      }
    }
    return JSON.stringify(value, null, 2);
  }
}
